import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BrandMark } from '../components/ui';

const Login: React.FC = () => {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const submitLockRef = useRef(false);
  const [retryUntil, setRetryUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const retrySeconds = Math.max(0, Math.ceil((retryUntil - now) / 1000));

  useEffect(() => {
    if (!retryUntil) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [retryUntil]);

  useEffect(() => {
    if (!authLoading && user) {
      switch (user.role) {
        case 'farmer':        navigate('/farmer-dashboard', { replace: true }); break;
        case 'driver':        navigate('/truck-portal',     { replace: true }); break;
        case 'field_agent':   navigate('/field-agent',      { replace: true }); break;
        case 'admin':         navigate('/admin',            { replace: true }); break;
        case 'finance':       navigate('/finance',          { replace: true }); break;
        case 'factory_clerk': navigate('/factory-clerk',    { replace: true }); break;
        default:              navigate('/dashboard',        { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLockRef.current || Date.now() < retryUntil) return;
    submitLockRef.current = true;
    setError(null);
    setLoading(true);
    try {
      await login(identifier.trim(), password);
    } catch (err: any) {
      if (err?.status === 429) {
        const seconds = Number.isFinite(err?.retryAfter) ? Math.max(1, err.retryAfter) : 30;
        setRetryUntil(Date.now() + seconds * 1000);
        setNow(Date.now());
      }
      setError(err.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    } finally {
      submitLockRef.current = false;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <BrandMark size="lg" accent="admin" />
          <p className="text-sm text-ink-500 dark:text-ink-400">Loading workspace…</p>
        </div>
      </div>
    );
  }

  if (user) return null;

  return (
    <div className="login-enterprise min-h-screen bg-ink-50 dark:bg-ink-950">
      <aside className="login-enterprise-brand brand-surface-cocoa text-white">
        <div className="login-enterprise-brand-pattern bg-dotted" aria-hidden />
        <div className="login-enterprise-brand-inner">
          <header className="login-enterprise-brand-header">
            <BrandMark size="lg" accent="cocoa" className="login-enterprise-logo" />
            <div>
              <p className="login-enterprise-brand-name">CoConnect</p>
              <p className="login-enterprise-brand-org">HEFARCAM · Cameroon</p>
            </div>
          </header>

          <section className="login-enterprise-story" aria-labelledby="login-brand-title">
            <p className="login-enterprise-kicker">Cocoa Value Chain Platform</p>
            <h1 id="login-brand-title" className="login-enterprise-headline">Traceable cocoa,<br />from farm to export.</h1>
            <p className="login-enterprise-summary">A unified operating system for cooperatives — farmer registry, batch traceability, payments, fleet logistics, EUDR compliance, and carbon credits in one platform.</p>

            <div className="login-enterprise-signals" aria-label="Platform capabilities">
              <div className="login-enterprise-signal"><span className="login-enterprise-signal-icon" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="4" y="4" width="6" height="6" rx="1.5"/><rect x="14" y="4" width="6" height="6" rx="1.5"/><rect x="4" y="14" width="6" height="6" rx="1.5"/><rect x="14" y="14" width="6" height="6" rx="1.5"/></svg></span><span><strong>6</strong><small>Production modules</small></span></div>
              <div className="login-enterprise-signal"><span className="login-enterprise-signal-icon" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 3l7 3v5c0 4.6-2.7 8-7 10-4.3-2-7-5.4-7-10V6l7-3z"/><path d="M9.2 12.2l1.8 1.8 3.9-4"/></svg></span><span><strong>EUDR</strong><small>Compliance workflows</small></span></div>
              <div className="login-enterprise-signal"><span className="login-enterprise-signal-icon" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="6" y="3" width="12" height="18" rx="2"/><path d="M9 7h6M10 17h4"/></svg></span><span><strong>PWA</strong><small>Offline-first operations</small></span></div>
            </div>

            <div className="login-enterprise-assurance" aria-label="Access safeguards">
              <span><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 3l7 3v5c0 4.6-2.7 8-7 10-4.3-2-7-5.4-7-10V6l7-3z"/><path d="M9.5 12l1.7 1.7 3.5-3.7"/></svg>Role-aware workspace access</span>
              <span><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l2.8 1.8"/></svg>Sign-in attempt throttling</span>
              <span><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M4 7h16M7 3v4M17 3v4M6 11h12v8H6z"/></svg>Authorized workspace routing</span>
            </div>
          </section>

          <footer className="login-enterprise-brand-footer"><span>© 2026 HEFARCAM</span><span aria-hidden>·</span><span>CoConnect v1.5</span></footer>
        </div>
      </aside>

      <main className="login-enterprise-auth">
        <div className="login-enterprise-mobile-brand md:hidden">
          <BrandMark size="md" accent="admin" />
          <div><p className="text-sm font-semibold text-ink-900 dark:text-ink-50">CoConnect</p><p className="text-xs text-ink-500 dark:text-ink-400">HEFARCAM · Cameroon</p></div>
        </div>

        <div className="login-enterprise-auth-inner">
          <div className="login-enterprise-form-shell">
            <div className="login-enterprise-form-heading">
              <div className="login-enterprise-access-badge"><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 018 0v3"/></svg><span>Staff access</span></div>
              <h2>Welcome back</h2>
              <p>Sign in with your staff credentials to continue to your authorized workspace.</p>
            </div>

            {error && (
              <div id="login-error" role="alert" aria-live="assertive" className="login-enterprise-alert">
                <svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 17h.01"/></svg><span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-enterprise-form" aria-describedby={error ? 'login-error' : undefined}>
              <div className="login-enterprise-field">
                <label htmlFor="identifier">Email or phone</label>
                <div className="login-enterprise-input-wrap"><span className="login-enterprise-field-icon" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M4 5h16v14H4z"/><path d="M4 7l8 6 8-6"/></svg></span><input id="identifier" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required autoComplete="username" autoCapitalize="none" spellCheck={false} aria-invalid={Boolean(error)} className="input login-enterprise-input" placeholder="you@hefarcam.cm or +2376XXXXXXXX" /></div>
              </div>

              <div className="login-enterprise-field">
                <label htmlFor="password">Password</label>
                <div className="login-enterprise-input-wrap"><span className="login-enterprise-field-icon" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 018 0v3"/></svg></span><input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" aria-invalid={Boolean(error)} className="input login-enterprise-input login-enterprise-password-input" placeholder="Enter your password"/><button type="button" onClick={() => setShowPassword(!showPassword)} className="login-enterprise-password-toggle" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword}>{showPassword ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.4 5.1A9.5 9.5 0 0112 5c5 0 9 4.5 10 7-.5 1.4-1.6 3.2-3.2 4.7M6.1 6.1C3.9 7.4 2.4 9.4 2 12c1 2.5 5 7 10 7 1.4 0 2.7-.3 3.9-.8"/></svg> : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>}</button></div>
              </div>

              <button type="submit" disabled={loading || retrySeconds > 0} className="login-enterprise-submit">
                {loading ? <><svg className="login-enterprise-spinner" viewBox="0 0 24 24" fill="none" aria-hidden><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity=".24"/><path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg><span>Signing in…</span></> : retrySeconds > 0 ? <><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg><span>Try again in {retrySeconds}s</span></> : <><span>Sign in to workspace</span><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg></>}
              </button>
            </form>

            <div className="login-enterprise-routing-note"><svg viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="12" r="9"/><path d="M12 10v6M12 7h.01"/></svg><span>Access is routed according to the role assigned to your account.</span></div>

            <div className="login-enterprise-portals">
              <div className="login-enterprise-divider"><span>Other workspaces</span></div>
              <div className="login-enterprise-portal-grid">
                <button type="button" onClick={() => navigate('/farmer-login')} className="login-enterprise-portal-button" aria-label="Open Farmer Portal sign-in"><span className="login-enterprise-portal-icon farmer" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 20V9M12 13c-3.8 0-6-2-6-5 3.8 0 6 2 6 5zM12 11c3.8 0 6-2 6-5-3.8 0-6 2-6 5z"/><path d="M7 20h10"/></svg></span><span><strong>Farmer Portal</strong><small>Farm & batch access</small></span><svg className="login-enterprise-portal-arrow" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M9 5l7 7-7 7"/></svg></button>
                <button type="button" onClick={() => navigate('/truck-login')} className="login-enterprise-portal-button" aria-label="Open Driver Portal sign-in"><span className="login-enterprise-portal-icon driver" aria-hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg></span><span><strong>Driver Portal</strong><small>Trips & logistics access</small></span><svg className="login-enterprise-portal-arrow" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M9 5l7 7-7 7"/></svg></button>
              </div>
              <p className="login-enterprise-support">Need access or credential recovery? Contact your HEFARCAM administrator.</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
